/** Normalized label|value keys for cross-section duplicate detection. */
function normalizeFactText(value: string): string {
  return value
    .replace(/[\u201C\u201D\u05F4"]/g, '')
    .replace(/\s*\^\s*/g, '. ')
    .replace(/\s*\/\s*/g, '/')
    .replace(/\s*,\s*/g, ', ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

export function factKey(label: string, value: string): string {
  return `${normalizeFactText(label)}|${normalizeFactText(value)}`;
}

export function valueKey(value: string): string {
  return factKey('', value).slice(1);
}

export function isDuplicateFact(
  label: string,
  value: string,
  seen: Set<string>,
): boolean {
  const v = value.trim();
  if (!v) return true;
  return seen.has(factKey(label, v)) || seen.has(valueKey(v));
}

export function rememberFact(label: string, value: string, seen: Set<string>): void {
  const v = value.trim();
  if (!v) return;
  seen.add(factKey(label, v));
  seen.add(valueKey(v));
}

type ParcelRow = { gush: number; helka: number; area_sqm: string };

export function collectSection1FactKeys(
  raw: {
    settlement?: string;
    gush?: number;
    helka?: number;
    area_sqm?: string;
    rows?: { label: string; value: string }[];
  },
  labels: { settlement: string; gush: string; helka: string; areaSqm: string },
): Set<string> {
  const keys = new Set<string>();
  if (raw.settlement?.trim()) {
    rememberFact(labels.settlement, raw.settlement, keys);
  }
  if (raw.gush != null) {
    rememberFact(labels.gush, String(raw.gush), keys);
  }
  if (raw.helka != null) {
    rememberFact(labels.helka, String(raw.helka), keys);
  }
  if (raw.area_sqm?.trim()) {
    rememberFact(labels.areaSqm, raw.area_sqm, keys);
  }
  for (const row of raw.rows ?? []) {
    if (row.label?.trim() && row.value?.trim()) {
      rememberFact(row.label, row.value, keys);
    }
  }
  return keys;
}

export function singleParcelDuplicatesScalars(
  parcels: ParcelRow[],
  gush?: number,
  helka?: number,
  area_sqm?: string,
): boolean {
  if (parcels.length !== 1) return false;
  const p = parcels[0];
  if (gush != null && p.gush !== gush) return false;
  if (helka != null && p.helka !== helka) return false;
  if (area_sqm?.trim()) {
    const norm = (s: string) => s.replace(/[,\s]/g, '').toLowerCase();
    if (norm(p.area_sqm) !== norm(area_sqm)) return false;
  }
  return true;
}
